import React, { useMemo } from 'react';
import { Tooltip } from 'design-system';
import IsomorphicLink from 'react-scale/corp/components/UI/IsomorphicLink';

export interface IHeatmapItem {
  value: any[];
  tooltip?: string;
  onClick?: () => void;
  href?: string;
}

interface IHeatmapKey {
  label: string;
  color: string;
  count: number;
}

const Heatmap: React.FC<{
  items: IHeatmapItem[];
  colorOfValue: (value: any) => string;
}> = ({ items, colorOfValue }) => {
  const sizeHeuristic = items.length < 10 ? 20 : items.length < 25 ? 16 : 14;

  const keys = useMemo(() => {
    const byCategory: Record<string, IHeatmapKey> = {};
    for (const { value } of items) {
      if (!value) continue;
      for (const item of value) {
        if (typeof item === 'undefined') continue;
        if (!byCategory[item]) {
          byCategory[item] = {
            color: colorOfValue(item),
            count: 0,
            label: item,
          };
        }
        byCategory[item].count++;
      }
    }
    return Object.values(byCategory).sort((a, b) => b.count - a.count);
  }, [items, colorOfValue]);

  if (!items.length) return <span />;

  const listOfColorsToCss = (colors: string[]) => {
    if (!colors.length) return {};
    if (colors.length === 1) return { background: colors[0] };
    let rule = `linear-gradient(0deg`;
    colors = colors.slice().sort();
    for (let i = 0; i < colors.length; ++i) {
      rule += ', ';
      const startPct = ((100 * i) / colors.length).toFixed(2);
      const endPct = ((100 * (i + 1)) / colors.length).toFixed(2);
      rule += `${colors[i]} ${startPct}%, ${colors[i]} ${endPct}%`;
    }
    rule += ')';
    return {
      background: rule,
    };
  };

  return (
    <div>
      <style jsx>{`
        .heatmap {
          display: flex;
          flex-wrap: wrap;
        }

        .heatmap-item {
          width: ${sizeHeuristic}px;
          height: ${sizeHeuristic}px;
          cursor: pointer;
          border: 1px solid #eee;
        }

        .heatmap-item:hover {
          border: 1px solid black;
        }

        .heatmap-keys {
          display: flex;
          margin-top: 10px;
        }

        .heatmap-key {
          display: flex;
          align-items: center;
          margin-right: 5px;
        }

        .heatmap-key__chip {
          border: 1px solid #888;
          border-radius: 3px;
          width: 14px;
          height: 14px;
          margin-right: 5px;
        }
      `}</style>
      <div className="heatmap">
        {items.map(({ value, tooltip, href, onClick }, index) => (
          <IsomorphicLink key={index} target={'_blank'} href={href ?? null}>
            <Tooltip key={index} title={tooltip ?? value.join(', ')} placement="top">
              <div
                onClick={onClick}
                className="heatmap-item"
                style={listOfColorsToCss(value.map(v => colorOfValue(v)))}
              />
            </Tooltip>
          </IsomorphicLink>
        ))}
      </div>
      <div className="heatmap-keys">
        {keys &&
          keys.map(({ label, color, count }) => (
            <div key={label} className="heatmap-key">
              <div className="heatmap-key__chip" style={{ background: color }} />
              {label}{' '}
              {typeof count !== 'undefined' && <span style={{ marginLeft: '5px' }}>({count})</span>}
            </div>
          ))}
      </div>
    </div>
  );
};

export default React.memo(Heatmap);
